import {
  createPrintError,
  createPrintResult,
  PRINT_ERROR_CODES,
} from "../contracts";
import { renderEscPos } from "../escpos";

const DEFAULT_BRIDGE_URL = "http://127.0.0.1:18181";
const DEFAULT_TIMEOUT_MS = 10000;
const MAX_PRINT_PAYLOAD_BYTES = 1024 * 1024;

function getBridgeUrl(printer = {}) {
  const value = printer?.bridge_url || DEFAULT_BRIDGE_URL;
  return String(value).replace(/\/+$/, "");
}

function getBridgeToken(printer = {}) {
  return printer?.bridge_token ? String(printer.bridge_token) : "";
}

function getPrinterId(printer = {}) {
  return printer?.printer_id || "";
}

function bytesToBase64(bytes) {
  const array = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);

  let binary = "";
  const chunkSize = 0x8000;

  for (let index = 0; index < array.length; index += chunkSize) {
    const chunk = array.subarray(index, index + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  return btoa(binary);
}

async function bridgeRequest(
  path,
  {
    method = "GET",
    body = null,
    printer = {},
    timeoutMs = DEFAULT_TIMEOUT_MS,
    idempotencyKey = "",
  } = {}
) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  const headers = {
    Accept: "application/json",
  };

  const token = getBridgeToken(printer);

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (body !== null) {
    headers["Content-Type"] = "application/json";
  }

  if (idempotencyKey) {
    headers["Idempotency-Key"] = idempotencyKey;
  }

  try {
    const response = await fetch(`${getBridgeUrl(printer)}${path}`, {
      method,
      headers,
      body: body === null ? undefined : JSON.stringify(body),
      signal: controller.signal,
    });

    const contentType = response.headers.get("content-type") || "";

    let payload = null;

    if (contentType.includes("application/json")) {
      payload = await response.json();
    } else {
      const text = await response.text();
      payload = text ? { message: text } : null;
    }

    if (!response.ok) {
      const error = new Error(
        payload?.message ||
          payload?.error ||
          `GLOO Print Bridge HTTP ${response.status}`
      );

      error.http_status = response.status;
      error.bridge_payload = payload;

      if (response.status === 401 || response.status === 403) {
        error.code = PRINT_ERROR_CODES.CONNECTION_FAILED;
      } else if (response.status === 404) {
        error.code = PRINT_ERROR_CODES.ADAPTER_UNAVAILABLE;
      } else if (response.status === 408) {
        error.code = PRINT_ERROR_CODES.TIMEOUT;
      } else {
        error.code = payload?.code || PRINT_ERROR_CODES.CONNECTION_FAILED;
      }

      throw error;
    }

    return payload;
  } catch (error) {
    if (error?.name === "AbortError") {
      throw createPrintError(
        PRINT_ERROR_CODES.TIMEOUT,
        "GLOO Print Bridge tidak merespons dalam batas waktu.",
        { cause: error }
      );
    }

    if (error?.code) {
      throw error;
    }

    throw createPrintError(
      PRINT_ERROR_CODES.BRIDGE_NOT_RUNNING,
      "GLOO Print Bridge Android tidak dapat dihubungi.",
      { cause: error }
    );
  } finally {
    clearTimeout(timeout);
  }
}

export async function checkAndroidBridge(printer = {}) {
  return bridgeRequest("/health", {
    method: "GET",
    printer,
  });
}

export async function getAndroidPrinters(printer = {}) {
  return bridgeRequest("/printers", {
    method: "GET",
    printer,
  });
}

export async function connectAndroidPrinter(printer = {}) {
  const printerId = getPrinterId(printer);

  if (!printerId) {
    throw createPrintError(
      PRINT_ERROR_CODES.PRINTER_NOT_FOUND,
      "Printer Android belum dipilih.",
      { adapter: "android_bridge" }
    );
  }

  const result = await bridgeRequest("/connect", {
    method: "POST",
    printer,
    body: {
      printer_id: printerId,
    },
  });

  if (result?.success === false) {
    throw createPrintError(
      result.code || PRINT_ERROR_CODES.CONNECTION_FAILED,
      result.message || "Gagal menghubungkan printer Android.",
      { adapter: "android_bridge" }
    );
  }

  return result;
}

export async function disconnectAndroidPrinter(printer = {}) {
  const printerId = getPrinterId(printer);

  if (!printerId) {
    return {
      success: true,
      status: "disconnected",
    };
  }

  return bridgeRequest("/disconnect", {
    method: "POST",
    printer,
    body: {
      printer_id: printerId,
    },
  });
}

export async function getAndroidPrinterStatus(printer = {}) {
  const printerId = getPrinterId(printer);

  if (!printerId) {
    throw createPrintError(
      PRINT_ERROR_CODES.PRINTER_NOT_FOUND,
      "Printer Android belum dipilih.",
      { adapter: "android_bridge" }
    );
  }

  const query = new URLSearchParams({
    printer_id: printerId,
  });

  return bridgeRequest(`/status?${query.toString()}`, {
    method: "GET",
    printer,
  });
}

export async function printAndroidBridgeAdapter({ job }) {
  if (!job?.printer) {
    throw createPrintError(
      PRINT_ERROR_CODES.PRINTER_NOT_CONFIGURED,
      "Profil printer Android belum tersedia.",
      {
        job,
        adapter: "android_bridge",
      }
    );
  }

  const printerId = getPrinterId(job.printer);

  if (!printerId) {
    throw createPrintError(
      PRINT_ERROR_CODES.PRINTER_NOT_FOUND,
      "Printer Android belum dipilih.",
      {
        job,
        adapter: "android_bridge",
      }
    );
  }

  let payload;

  try {
    payload = renderEscPos(job.content || {}, job.printer);
  } catch (error) {
    throw createPrintError(
      error?.code || PRINT_ERROR_CODES.ENCODING_ERROR,
      error?.message || "Gagal membuat payload ESC/POS.",
      {
        job,
        adapter: "android_bridge",
        cause: error,
      }
    );
  }

  const payloadBytes =
    payload instanceof Uint8Array ? payload : new Uint8Array(payload);

  if (payloadBytes.byteLength > MAX_PRINT_PAYLOAD_BYTES) {
    throw createPrintError(
      PRINT_ERROR_CODES.PRINT_JOB_REJECTED,
      "Ukuran print job melebihi batas 1 MB.",
      {
        job,
        adapter: "android_bridge",
      }
    );
  }

  const payloadBase64 = bytesToBase64(payloadBytes);

  try {
    const result = await bridgeRequest("/print", {
      method: "POST",
      printer: job.printer,
      idempotencyKey: job.job_id,
      body: {
        job_id: job.job_id,
        printer_id: printerId,
        protocol: job.printer.protocol || "escpos",
        encoding: job.printer.encoding || "utf-8",
        copies: job.copies || 1,
        payload_base64: payloadBase64,
      },
    });

    if (result?.success === false) {
      throw createPrintError(
        result.code || PRINT_ERROR_CODES.PRINT_JOB_REJECTED,
        result.message || "GLOO Print Bridge menolak print job.",
        {
          job,
          adapter: "android_bridge",
        }
      );
    }

    return createPrintResult({
      job,
      success: true,
      adapter: "android_bridge",
      transport_result: result,
    });
  } catch (error) {
    if (error?.code) {
      error.job_id = error.job_id || job.job_id;
      error.adapter = error.adapter || "android_bridge";
      throw error;
    }

    throw createPrintError(
      PRINT_ERROR_CODES.WRITE_FAILED,
      error?.message || "Gagal mengirim print job ke GLOO Print Bridge.",
      {
        job,
        adapter: "android_bridge",
        cause: error,
      }
    );
  }
}

export {
  DEFAULT_BRIDGE_URL,
  DEFAULT_TIMEOUT_MS,
  MAX_PRINT_PAYLOAD_BYTES,
};
